import React, { Fragment, useMemo } from 'react';
import { buildEditorUrl } from '../shared/liveProtocol';
import {
  getGoLiveHelperDownloadTargets,
  LOCAL_HELPER_DOWNLOAD_TARGETS,
  getLocalHelperPlatform,
} from './localHelperClient';
import './helperDownload.css';

const getDefaultLocationLike = () => (
  typeof window === 'undefined' ? null : window.location
);

const getDefaultNavigatorLike = () => (
  typeof navigator === 'undefined' ? null : navigator
);

const createFallbackTargets = () => ({
  windows: {
    platform: 'windows',
    label: 'Windows',
    url: '',
    isConfigured: false,
    envVarName: 'REACT_APP_GOLIVE_HELPER_WINDOWS_ZIP_URL',
  },
  macArm64: {
    platform: LOCAL_HELPER_DOWNLOAD_TARGETS.MAC_ARM64,
    label: 'Mac (Silicon)',
    url: '',
    isConfigured: false,
    envVarName: 'REACT_APP_GOLIVE_HELPER_MAC_ARM64_ZIP_URL',
  },
  macX64: {
    platform: LOCAL_HELPER_DOWNLOAD_TARGETS.MAC_X64,
    label: 'Mac (Intel)',
    url: '',
    isConfigured: false,
    envVarName: 'REACT_APP_GOLIVE_HELPER_MAC_X64_ZIP_URL',
  },
});

const mergeDownloadTargets = (value = {}) => {
  const fallback = createFallbackTargets();
  return {
    windows: {
      ...fallback.windows,
      ...(value?.windows || {}),
    },
    macArm64: {
      ...fallback.macArm64,
      ...(value?.macArm64 || {}),
    },
    macX64: {
      ...fallback.macX64,
      ...(value?.macX64 || {}),
    },
  };
};

const PLATFORM_COPY = {
  windows: {
    title: 'Windows',
    buttonLabel: 'Download Windows ZIP',
    compatibilityNote: 'Compatible with Windows 10 & 11 (64-bit).',
    steps: [
      'Download the latest ZIP archive.',
      'Extract it to any folder.',
      'Run GoLive Helper.exe.',
    ],
    missingHeading: 'Windows ZIP missing',
    missingCopy: 'Add the public Windows helper ZIP for this deployment.',
  },
  mac: {
    title: 'macOS',
    compatibilityNote: 'Apple Silicon is recommended for most Macs. Intel is only for older Macs.',
    steps: [
      'Download the ZIP for your Mac.',
      'Move GoLive Helper.app to Applications.',
      'Choose Open Anyway if macOS prompts you.',
    ],
    missingHeading: 'macOS downloads missing',
    missingCopy: 'Add at least one public Mac helper ZIP for this deployment.',
  },
};

const joinEnvVarNames = (envVarNames = []) => envVarNames.filter(Boolean);

const DownloadControl = ({
  label,
  href = '',
  variant = 'primary',
  disabled = false,
} = {}) => {
  const className = `helper-download-button helper-download-button-${variant}`.trim();

  if (!disabled && href) {
    return (
      <a className={className} href={href} target="_blank" rel="noreferrer">
        {label}
      </a>
    );
  }

  return (
    <button type="button" className={className} disabled>
      {label}
    </button>
  );
};

const MaintainerNote = ({
  title,
  copy,
  envVarNames = [],
} = {}) => {
  const resolvedEnvVarNames = joinEnvVarNames(envVarNames);

  return (
    <div className="helper-download-maintainer-note">
      <strong>{title}</strong>
      <p>{copy}</p>
      {resolvedEnvVarNames.length ? (
        <p>
          Set{' '}
          {resolvedEnvVarNames.map((envVarName, index) => (
            <Fragment key={envVarName}>
              {index > 0 ? (index === resolvedEnvVarNames.length - 1 ? ' and ' : ', ') : null}
              <code>{envVarName}</code>
            </Fragment>
          ))}
          {' '}in Vercel and redeploy.
        </p>
      ) : null}
    </div>
  );
};

const AppleIcon = () => (
  <svg
    className="helper-download-platform-icon-svg helper-download-platform-icon-svg-mac"
    data-testid="helper-download-platform-icon-mac"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fill="currentColor"
      d="M18.71 19.5C17.88 20.74 17 21.95 15.66 21.97C14.32 22 13.89 21.18 12.37 21.18C10.84 21.18 10.37 21.95 9.09997 22C7.78997 22.05 6.79997 20.68 5.95997 19.47C4.24997 17 2.93997 12.45 4.69997 9.39C5.56997 7.87 7.12997 6.91 8.81997 6.88C10.1 6.86 11.32 7.75 12.11 7.75C12.89 7.75 14.37 6.68 15.92 6.84C16.57 6.87 18.39 7.1 19.56 8.82C19.47 8.88 17.39 10.1 17.41 12.63C17.44 15.65 20.06 16.66 20.09 16.67C20.06 16.74 19.67 18.11 18.71 19.5ZM13 3.5C13.73 2.67 14.94 2.04 15.94 2C16.07 3.17 15.6 4.35 14.9 5.19C14.21 6.04 13.07 6.7 11.95 6.61C11.8 5.46 12.36 4.26 13 3.5Z"
    />
  </svg>
);

const WindowsCard = ({
  target,
  isDetected = false,
} = {}) => {
  const copy = PLATFORM_COPY.windows;

  return (
    <article
      className={`helper-download-platform-card helper-download-platform-card-windows ${isDetected ? 'is-detected' : ''} ${target.isConfigured ? '' : 'is-missing'}`.trim()}
      aria-label="Windows helper download"
    >
      <div className="helper-download-platform-header">
        <span className="helper-download-platform-icon helper-download-platform-icon-windows" aria-hidden="true" />
        <div className="helper-download-platform-heading">
          <div className="helper-download-platform-title-row">
            <h2>{copy.title}</h2>
            {isDetected ? <span className="helper-download-card-badge">This device</span> : null}
          </div>
        </div>
      </div>

      <ol className="helper-download-step-list">
        {copy.steps.map((step) => (
          <li key={step}>{step}</li>
        ))}
      </ol>

      <div className="helper-download-card-actions">
        <DownloadControl
          label={copy.buttonLabel}
          href={target.url}
          disabled={!target.isConfigured}
        />
        <p className="helper-download-card-meta">{copy.compatibilityNote}</p>
        {!target.isConfigured ? (
          <MaintainerNote
            title={copy.missingHeading}
            copy={copy.missingCopy}
            envVarNames={[target.envVarName]}
          />
        ) : null}
      </div>
    </article>
  );
};

const getMacMaintainerNote = (targets = []) => {
  const missingTargets = targets.filter((target) => !target.isConfigured);
  if (!missingTargets.length) return null;

  if (missingTargets.length === targets.length) {
    return {
      title: PLATFORM_COPY.mac.missingHeading,
      copy: PLATFORM_COPY.mac.missingCopy,
      envVarNames: missingTargets.map((target) => target.envVarName),
    };
  }

  const [missingTarget] = missingTargets;
  return {
    title: `${missingTarget.label} missing`,
    copy: `Add the public ${missingTarget.label} helper ZIP for this deployment.`,
    envVarNames: [missingTarget.envVarName],
  };
};

const MacCard = ({
  macArm64Target,
  macX64Target,
  isDetected = false,
} = {}) => {
  const copy = PLATFORM_COPY.mac;
  const hasConfiguredTarget = macArm64Target.isConfigured || macX64Target.isConfigured;
  const maintainerNote = getMacMaintainerNote([macArm64Target, macX64Target]);

  return (
    <article
      className={`helper-download-platform-card helper-download-platform-card-mac ${isDetected ? 'is-detected' : ''} ${hasConfiguredTarget ? '' : 'is-missing'}`.trim()}
      aria-label="macOS helper download"
    >
      <div className="helper-download-platform-header">
        <span className="helper-download-platform-icon helper-download-platform-icon-mac" aria-hidden="true">
          <AppleIcon />
        </span>
        <div className="helper-download-platform-heading">
          <div className="helper-download-platform-title-row">
            <h2>{copy.title}</h2>
            {isDetected ? <span className="helper-download-card-badge">This device</span> : null}
          </div>
        </div>
      </div>

      <ol className="helper-download-step-list">
        {copy.steps.map((step) => (
          <li key={step}>{step}</li>
        ))}
      </ol>

      <div className="helper-download-button-row">
        <DownloadControl
          label="Mac (Silicon)"
          href={macArm64Target.url}
          disabled={!macArm64Target.isConfigured}
        />
        <DownloadControl
          label="Mac (Intel)"
          href={macX64Target.url}
          variant="secondary"
          disabled={!macX64Target.isConfigured}
        />
      </div>

      <p className="helper-download-card-meta">{copy.compatibilityNote}</p>
      {maintainerNote ? (
        <MaintainerNote
          title={maintainerNote.title}
          copy={maintainerNote.copy}
          envVarNames={maintainerNote.envVarNames}
        />
      ) : null}
    </article>
  );
};

const GoLiveHelperDownloadApp = ({
  downloadTargets,
  detectedPlatform,
  editorUrl,
} = {}) => {
  const resolvedDownloadTargets = useMemo(
    () => (
      downloadTargets
        ? mergeDownloadTargets(downloadTargets)
        : getGoLiveHelperDownloadTargets({ locationLike: getDefaultLocationLike() })
    ),
    [downloadTargets],
  );
  const resolvedDetectedPlatform = useMemo(
    () => (detectedPlatform || getLocalHelperPlatform(getDefaultNavigatorLike())),
    [detectedPlatform],
  );
  const resolvedEditorUrl = useMemo(
    () => (typeof editorUrl === 'string' ? editorUrl : buildEditorUrl(getDefaultLocationLike())),
    [editorUrl],
  );

  const hasAnyConfiguredDownload = Object.values(resolvedDownloadTargets).some((target) => target.isConfigured);
  const isWindowsDetected = resolvedDetectedPlatform === 'windows';
  const isMacDetected = resolvedDetectedPlatform === 'mac';

  return (
    <main className="helper-download-shell">
      <div className="helper-download-top-actions">
        <a className="helper-download-topbar-link" href={resolvedEditorUrl}>Back to Editor</a>
      </div>

      <section className={`helper-download-hero ${hasAnyConfiguredDownload ? '' : 'helper-download-hero-missing'}`.trim()}>
        <span className={`helper-download-hero-kicker ${hasAnyConfiguredDownload ? '' : 'helper-download-hero-kicker-warn'}`.trim()}>
          {hasAnyConfiguredDownload ? 'Local mode helper' : 'Maintainer state'}
        </span>
        <h1>{hasAnyConfiguredDownload ? 'Download GoLive Helper' : 'Helper downloads are not configured'}</h1>
        <p>
          {hasAnyConfiguredDownload
            ? 'Choose the build for your computer, open it once, then head back to AnimCue and use Local mode.'
            : 'Publish at least one public helper ZIP and this page will switch back to the public install flow.'}
        </p>
      </section>

      <section className="helper-download-platform-grid">
        <WindowsCard
          target={resolvedDownloadTargets.windows}
          isDetected={isWindowsDetected}
        />
        <MacCard
          macArm64Target={resolvedDownloadTargets.macArm64}
          macX64Target={resolvedDownloadTargets.macX64}
          isDetected={isMacDetected}
        />
      </section>

      <div className="helper-download-return">
        <a className="helper-download-return-link" href={resolvedEditorUrl}>Back to Editor</a>
      </div>

      {hasAnyConfiguredDownload ? (
        <section className="helper-download-note" aria-label="Helper note">
          <div className="helper-download-note-marker" aria-hidden="true">i</div>
          <div className="helper-download-note-copy">
            <h2>Keep the helper open</h2>
            <p>Leave GoLive Helper running in the background while you use Local mode so the editor stays connected.</p>
          </div>
        </section>
      ) : null}
    </main>
  );
};

export default GoLiveHelperDownloadApp;
